// Browser game review: fast pass over every position, then depth-16 re-check of flagged user moves.
import { Chess } from "chess.js";
import { getEngine, ENGINE_NAME } from "./browserEngine";

export const DEEP_DEPTH = 16;
export const REVIEW_MODES = {
  thorough: { scanMs: 300, deepDepth: DEEP_DEPTH, maxTargets: 8 },
  quick: { scanMs: 150, deepDepth: 12, maxTargets: 3 },
};
const MATE_CP = 10000;
const EVAL_CLAMP = 1500;
const MAX_PLIES = 240;
const FLAGGED = ["inaccuracy", "mistake", "blunder"];
const VALUES = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };

export class ReviewError extends Error {
  constructor(stage, message) {
    super(message);
    this.stage = stage;
  }
}

export function parseGame(pgn) {
  const game = new Chess();
  try {
    game.loadPgn(pgn);
  } catch (e) {
    const m = /Invalid move in PGN: (\S+)/.exec(e.message || "");
    throw new ReviewError("parse", m ? `${m[1]} is illegal in that position — check the PGN.` : "Could not read PGN — paste the full game text.");
  }
  const history = game.history({ verbose: true }).slice(0, MAX_PLIES);
  if (!history.length) throw new ReviewError("parse", "No moves found in the PGN.");
  return history;
}

function terminalEval(fen) {
  const b = new Chess(fen);
  if (b.isCheckmate()) {
    const loserWhite = b.turn() === "w";
    return { cp: loserWhite ? -MATE_CP : MATE_CP, mate: null, loserWhite, pv: [], terminal: true };
  }
  if (b.isStalemate() || b.isInsufficientMaterial()) return { cp: 0, mate: null, loserWhite: null, pv: [], terminal: true };
  return null;
}

// White-perspective eval of a FEN (engine reports side-to-move).
async function evalFen(fen, opts) {
  const t = terminalEval(fen);
  if (t) return t;
  let r;
  try {
    r = await getEngine().analyse(fen, opts);
  } catch {
    return null;
  }
  if (!r || (r.cp == null && r.mate == null)) return null;
  const sign = fen.split(" ")[1] === "w" ? 1 : -1;
  const mate = r.mate == null ? null : sign * r.mate;
  const cp = mate != null ? (mate > 0 ? MATE_CP - Math.abs(mate) : -MATE_CP + Math.abs(mate)) : sign * r.cp;
  return { cp, mate, loserWhite: null, pv: r.pv, terminal: false, depth: r.depth };
}

const clamp = (cp) => Math.max(-EVAL_CLAMP, Math.min(EVAL_CLAMP, cp));

function matedAgainst(e, moverWhite) {
  if (e.terminal) return e.loserWhite != null && e.loserWhite === moverWhite;
  return e.mate != null && (moverWhite ? e.mate < 0 : e.mate > 0);
}

function scoreMove(pos, eb, ea) {
  if (!eb || !ea) return { cp_before: null, cp_after: null, cpl: 0, allowed_mate: false, is_best_move: false, engine_failed: true, pv: [] };
  const sign = pos.mover_white ? 1 : -1;
  const allowedMate = matedAgainst(ea, pos.mover_white) && !matedAgainst(eb, pos.mover_white);
  const raw = clamp(sign * eb.cp) - clamp(sign * ea.cp);
  const top = eb.pv[0] || null;
  return {
    cp_before: eb.cp, cp_after: ea.cp,
    cpl: allowedMate ? 1000 : Math.min(1000, Math.max(0, raw)),
    allowed_mate: allowedMate, engine_failed: false,
    is_best_move: top != null && pos.move_uci === top, pv: eb.pv,
  };
}

function material(fen, color) {
  return new Chess(fen).board().flat().reduce((s, sq) => s + (sq && sq.color === color ? VALUES[sq.type] : 0), 0);
}

function isSacrifice(pos, next) {
  if (!next) return false;
  const color = pos.mover_white ? "w" : "b";
  return material(pos.fen_before, color) - material(next.fen_after, color) >= 3;
}

function severity(cpl, best, sac, ply) {
  if (best && cpl < 5 && ply >= 20 && sac) return "brilliant";
  if (best && cpl < 25) return "great";
  if (cpl < 50) return "solid";
  if (cpl < 100) return "inaccuracy";
  if (cpl < 200) return "mistake";
  return "blunder";
}

function classify(moves, i) {
  const s = moves[i];
  s.is_sacrifice = false;
  if (s.engine_failed) { s.classification = null; return; }
  if (s.allowed_mate) { s.classification = "blunder"; return; }
  const sac = s.is_best_move && s.cpl < 5 ? isSacrifice(s, moves[i + 1]) : false;
  s.classification = severity(s.cpl, s.is_best_move, sac, s.ply);
  s.is_sacrifice = sac;
}

/**
 * Runs the full engine review in the browser.
 * onProgress({stage: "loading"|"scan"|"deep", done, total}); isCancelled() aborts between positions.
 */
export async function reviewGame(pgn, color, { mode = "thorough", onProgress = () => {}, isCancelled = () => false } = {}) {
  const cfg = REVIEW_MODES[mode] || REVIEW_MODES.thorough;
  const history = parseGame(pgn);
  const isWhite = color === "white";
  const positions = history.map((h, ply) => ({
    ply, san: h.san, move_uci: h.from + h.to + (h.promotion || ""),
    fen_before: h.before, fen_after: h.after,
    mover_white: h.color === "w", is_user_move: (h.color === "w") === isWhite,
  }));

  onProgress({ stage: "loading", done: 0, total: 1 });
  try {
    await getEngine().init();
  } catch (e) {
    throw new ReviewError("engine", e.message);
  }

  const fens = [positions[0].fen_before, ...positions.map((p) => p.fen_after)];
  const evals = [];
  for (let i = 0; i < fens.length; i++) {
    if (isCancelled()) throw new ReviewError("cancelled", "Review cancelled.");
    evals.push(await evalFen(fens[i], { movetime: cfg.scanMs }));
    onProgress({ stage: "scan", done: i + 1, total: fens.length });
  }
  const failed = evals.filter((e) => e == null).length;
  if (failed > Math.floor(evals.length / 2)) {
    throw new ReviewError("engine", `The engine could not evaluate ${failed} of ${evals.length} positions on this device.`);
  }

  const moves = positions.map((p, i) => ({ ...p, ...scoreMove(p, evals[i], evals[i + 1]) }));
  moves.forEach((_, i) => classify(moves, i));

  const targets = moves
    .filter((m) => m.is_user_move && FLAGGED.includes(m.classification))
    .sort((a, b) => b.cpl - a.cpl || a.ply - b.ply)
    .slice(0, cfg.maxTargets);
  for (let k = 0; k < targets.length; k++) {
    if (isCancelled()) throw new ReviewError("cancelled", "Review cancelled.");
    const m = targets[k];
    onProgress({ stage: "deep", done: k, total: targets.length, depth: cfg.deepDepth });
    const eb = await evalFen(m.fen_before, { depth: cfg.deepDepth });
    const ea = await evalFen(m.fen_after, { depth: cfg.deepDepth });
    onProgress({ stage: "deep", done: k + 1, total: targets.length, depth: cfg.deepDepth });
    if (!eb || !ea) continue;
    Object.assign(m, scoreMove(m, eb, ea));
    m.deep_depth = Math.min(eb.depth ?? cfg.deepDepth, ea.depth ?? cfg.deepDepth, cfg.deepDepth);
    classify(moves, m.ply);
  }

  return {
    engine: { name: ENGINE_NAME, mode, scan_movetime_ms: cfg.scanMs, deep_depth: cfg.deepDepth, skipped_positions: failed },
    moves: moves.map((m) => ({
      ply: m.ply, move_uci: m.move_uci, cp_before: m.cp_before, cp_after: m.cp_after, cpl: m.cpl,
      classification: m.classification, allowed_mate: m.allowed_mate, is_best_move: m.is_best_move,
      is_sacrifice: m.is_sacrifice, engine_failed: m.engine_failed, deep_depth: m.deep_depth ?? null,
      best_line_uci: FLAGGED.includes(m.classification) && m.is_user_move ? m.pv.slice(0, 5) : [],
    })),
  };
}

/** Grade a retry move: White-perspective eval after the move + the engine's best move from the start position. */
export async function gradeMove(fenBefore, moveUci, knownBestUci) {
  const after = new Chess(fenBefore);
  const san = after.move({ from: moveUci.slice(0, 2), to: moveUci.slice(2, 4), promotion: moveUci[4] || "q" }).san;
  const ea = await evalFen(after.fen(), { depth: DEEP_DEPTH });
  let bestUci = knownBestUci;
  if (!bestUci) {
    const eb = await evalFen(fenBefore, { depth: DEEP_DEPTH });
    bestUci = eb?.pv?.[0] || null;
  }
  let bestSan = null;
  if (bestUci) {
    try {
      bestSan = new Chess(fenBefore).move({ from: bestUci.slice(0, 2), to: bestUci.slice(2, 4), promotion: bestUci[4] }).san;
    } catch { bestSan = null; }
  }
  return { san, cp_after: ea?.cp ?? null, best_move_uci: bestUci, best_move_san: bestSan, is_best: bestUci != null && moveUci === bestUci };
}

/** Engine data for a puzzle start position; both evals are from the solver's perspective. */
export async function analysePuzzle(startFen, firstSolutionUci) {
  const r = await getEngine().analyse(startFen, { depth: DEEP_DEPTH });
  const cpBefore = r.mate != null ? (r.mate > 0 ? MATE_CP - r.mate : -MATE_CP - r.mate) : r.cp;
  let cpAfterBest = null;
  if (firstSolutionUci) {
    const b = new Chess(startFen);
    try {
      b.move({ from: firstSolutionUci.slice(0, 2), to: firstSolutionUci.slice(2, 4), promotion: firstSolutionUci[4] });
      const ea = await evalFen(b.fen(), { depth: 12 });
      const solverSign = startFen.split(" ")[1] === "w" ? 1 : -1;
      cpAfterBest = ea ? solverSign * ea.cp : null;
    } catch { cpAfterBest = null; }
  }
  return { engine_pv_uci: r.pv.slice(0, 6), cp_before: cpBefore, cp_after_best: cpAfterBest };
}
